import { analects, analectById, canonicalId } from "../engine/analects";
import type { Analect } from "../engine/types";
import { CONCEPT_CATALOG, REVIEWED_RELATIONS, type ConceptEntry } from "./conceptCatalog";
import exams from "./data/exams.json";

// Only unambiguous glyph pairs used by this index. Sense-dependent 知/智,
// 弟/悌, 女/汝, 說/悅, 藏/臟 are deliberately NOT global substitutions.
export const GLYPH_PAIRS = "節节濟济優优寬宽猶犹禮礼學学習习義义為为愛爱樂乐詩诗書书賢贤聖圣過过恥耻憂忧貧贫賤贱貴贵問问見见聞闻誨诲啟启憤愤類类溫温舉举貫贯隱隐從从處处質质喪丧儉俭讓让鄉乡時时無无於于與与謹谨達达遠远邇迩親亲強强剛刚惡恶順顺殺杀身身觀观興兴眾众復复諸诸務务則则說说讒谗諂谄訥讷謀谋齊齐異异兌兑誠诚勇勇窮穷篤笃執执遜逊敬敬祿禄饑饥餓饿飲饮飯饭疏疏繪绘素素聽听辭辞祀祀祭祭諒谅誼谊藝艺遊游體体衛卫論论語语許许隨随進进退退悅悦誦诵駟驷馬马鳥鸟獸兽樊樊遲迟顏颜淵渊貢贡閔闵騫骞張张參参長长魯鲁堯尧舜舜湯汤羣群簡简恆恒權权貞贞";
const glyphs = new Map<string, string>();
for (let i = 0; i < GLYPH_PAIRS.length; i += 2) glyphs.set(GLYPH_PAIRS[i], GLYPH_PAIRS[i + 1]);
export function normalizeGlyphs(value: string): string {
  return [...value].map(c => glyphs.get(c) ?? c).join("");
}
export type MatchRange = { start: number; end: number; term: string };
export type ConceptHit = { passage: Analect; ranges: MatchRange[]; count: number; reasons: string[] };
export function textMatches(text: string, terms: readonly string[]): MatchRange[] {
  // Map normalized offsets back to the unchanged original; annotation numbers
  // and source line wrapping cannot hide a multi-character phrase.
  let plain = "";
  const offsets: number[] = [];
  for (let i = 0; i < text.length; i++) {
    if (/[⑴-⒇\s]/u.test(text[i])) continue;
    plain += normalizeGlyphs(text[i]); offsets.push(i);
  }
  const ranges = new Map<string, MatchRange>();
  for (const term of new Set(terms.map(t => normalizeGlyphs(t.trim())).filter(Boolean))) {
    for (let at = plain.indexOf(term); at !== -1; at = plain.indexOf(term, at + 1)) {
      const start = offsets[at], end = offsets[at + term.length - 1] + 1;
      ranges.set(`${start}:${end}`, { start, end, term });
    }
  }
  return [...ranges.values()].sort((a, b) => a.start - b.start || b.end - a.end);
}
export function literalHits(terms: readonly string[]): ConceptHit[] {
  return analects.map(passage => {
    const ranges = textMatches(passage.text, terms);
    return { passage, ranges, count: ranges.length, reasons: [] };
  }).filter(hit => hit.count > 0);
}
export function reviewedHits(conceptId: string): ConceptHit[] {
  const concept = CONCEPT_CATALOG.find(c => c.id === conceptId);
  const grouped = new Map<number, ConceptHit>();
  for (const relation of REVIEWED_RELATIONS.filter(r => r.concepts.includes(conceptId))) {
    const passage = analects.find(p => `${p.major}.${p.minor}` === relation.coordinate);
    if (!passage) continue; // Build-time tests require all references to resolve.
    const hit = grouped.get(passage.id) ?? { passage, ranges: textMatches(passage.text, concept?.terms ?? []), count: 0, reasons: [] };
    hit.reasons.push(relation.reason); hit.count = hit.ranges.length;
    grouped.set(passage.id, hit);
  }
  return [...grouped.values()].sort((a, b) => a.passage.major - b.passage.major || a.passage.minor - b.passage.minor);
}
export function resolveConcept(query: string): ConceptEntry | undefined {
  const normalized = normalizeGlyphs(query.trim());
  return CONCEPT_CATALOG.find(c => normalizeGlyphs(c.label) === normalized || c.aliases?.some(alias => normalizeGlyphs(alias) === normalized));
}
export function examsForPassage(id: number) {
  return exams.filter(exam => exam.passages.some(ref => canonicalId(ref) === canonicalId(id)));
}
export function highlightedSegments(text: string, ranges: MatchRange[], offset = 0) {
  const cuts = new Set([0, text.length]);
  for (const range of ranges) if (range.end > offset && range.start < offset + text.length) {
    cuts.add(Math.max(0, range.start - offset)); cuts.add(Math.min(text.length, range.end - offset));
  }
  const sorted = [...cuts].sort((a, b) => a - b);
  return sorted.slice(0, -1).map((start, i) => ({ text: text.slice(start, sorted[i + 1]), match: ranges.some(r => r.start <= start + offset && r.end > start + offset) }));
}
export const INDEX_VERSION = "analects-concepts-v1";
export function validPassageId(value: unknown): number | null {
  return typeof value === "number" && analectById.has(value) ? canonicalId(value) : null;
}
